import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChangeLog, Pagination, Response } from '@ui/src';
import { exchangeAutoSettingsApi } from '../services';
import { AutoExchangeSettings, Category } from '../../types';

export interface AutoSettingsDetailsState {
  autoSettingsDetails?: AutoExchangeSettings;
  defaultSettings?: AutoExchangeSettings;
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  accountCategories?: Category[];
}

const initialState: AutoSettingsDetailsState = {};

export const autoSettingsDetailsSlice = createSlice({
  name: 'autoSettingsDetails',
  initialState,
  reducers: {
    clearAutoSettingsDetailsState: (state) => {
      state.autoSettingsDetails = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
    },
    clearDefaultAutoSettingsDetailsState: (state) => {
      state.defaultSettings = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      exchangeAutoSettingsApi.endpoints.getExchangeAutoSettingsItem
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AutoExchangeSettings }>) => {
        state.autoSettingsDetails = action.payload.data;
      },
    );
    builder.addMatcher(
      exchangeAutoSettingsApi.endpoints.getAutoExchangeAccountsCategories
        .matchFulfilled,
      (state, action: PayloadAction<Response<Category>>) => {
        state.accountCategories = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      exchangeAutoSettingsApi.endpoints.getExchangeAutoSettingsDefault
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AutoExchangeSettings }>) => {
        state.defaultSettings = action.payload.data;
      },
    );
    builder.addMatcher(
      exchangeAutoSettingsApi.endpoints.getRuleChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
  },
});

export const {
  clearAutoSettingsDetailsState,
  clearDefaultAutoSettingsDetailsState,
} = autoSettingsDetailsSlice.actions;

export default autoSettingsDetailsSlice.reducer;
